import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { AppExceptionModel } from '../../models/app-exception.model';
import { SchoolMember } from '../../models/member.school.model';
import * as gradeActions from '../actions/grade.school.actions';
import * as schoolMemberActions from '../actions/member.school.actions';

export interface SchoolMemberState extends EntityState<SchoolMember> {
  loading: boolean;
  loaded: boolean;
  inProgress: boolean;
  memberStatsLoading: boolean;
  error: AppExceptionModel | undefined;
}

export const schoolMemberAdapter: EntityAdapter<SchoolMember> =
  createEntityAdapter<SchoolMember>();

export const initialState: SchoolMemberState =
  schoolMemberAdapter.getInitialState({
    loading: false,
    loaded: false,
    inProgress: false,
    memberStatsLoading: false,
    error: undefined,
  });

export const schoolMemberReducer = createReducer(
  initialState,
  on(schoolMemberActions.getMembers, (state, {}) => ({
    ...state,
    loading: true,
    loaded: false,
    error: undefined,
  })),
  on(schoolMemberActions.getMember, (state, {}) => ({
    ...state,
    error: undefined,
  })),
  on(schoolMemberActions.getMembersDone, (state, {}) => ({
    ...state,
    loading: false,
    error: undefined,
  })),
  on(schoolMemberActions.addMemberToStore, (state, { member }) =>
    schoolMemberAdapter.upsertOne(member, {
      ...state,
      error: undefined,
      loading: false,
      loaded: true,
    }),
  ),
  on(schoolMemberActions.addMembersToStore, (state, { members }) => {
    return schoolMemberAdapter.upsertMany(members, {
      ...state,
      loading: false,
      loaded: true,
      inProgress: false,
      error: undefined,
    });
  }),
  on(schoolMemberActions.getMembersFailure, (state, { error }) => ({
    ...state,
    loading: false,
    loaded: true,
    error: error,
  })),
  on(schoolMemberActions.update, (state, { member }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(schoolMemberActions.updateSuccess, (state, { member }) =>
    schoolMemberAdapter.upsertOne(member, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(schoolMemberActions.updateFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(schoolMemberActions.updateStatus, (state, { member }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(schoolMemberActions.updateStatusSuccess, (state, { member }) =>
    schoolMemberAdapter.upsertOne(member, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(schoolMemberActions.updateStatusFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(gradeActions.reassignGradeSuccess, (state) => ({
    ...state,
    error: undefined,
    inProgress: false,
  })),
  on(schoolMemberActions.linkParent, (state) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(schoolMemberActions.linkParentFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(schoolMemberActions.linkParentSuccess, (state, { student, parent }) =>
    schoolMemberAdapter.upsertMany([student, parent], {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(schoolMemberActions.delinkChild, (state, { student, parent }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(schoolMemberActions.delinkChildSuccess, (state, { student, parent }) =>
    schoolMemberAdapter.upsertMany([student, parent], {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(schoolMemberActions.delinkChildFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(schoolMemberActions.deleteAction, (state, { id }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(schoolMemberActions.deleteSuccess, (state, { id }) =>
    schoolMemberAdapter.removeOne(id, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(schoolMemberActions.deleteFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(schoolMemberActions.getMemberStats, (state) => ({
    ...state,
    memberStatsLoading: true,
  })),
  on(schoolMemberActions.getMemberStatsDone, (state) => ({
    ...state,
    memberStatsLoading: false,
  })),
  on(schoolMemberActions.getMemberStatsFailure, (state) => ({
    ...state,
    memberStatsLoading: false,
  })),
  on(schoolMemberActions.clearFromStore, (state, { ids }) =>
    schoolMemberAdapter.removeMany(ids, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(schoolMemberActions.clear, (state) => initialState),
);
